import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useCurrency } from '@/hooks/useCurrency';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { SeoHead } from '@/components/seo/SeoHead';
import { supabase } from '@/integrations/supabase/client';
import ScheduleMockup from '@/components/landing/ScheduleMockup';
import logo from '@/assets/logo.png';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import {
  Calendar,
  Users,
  ArrowRight,
  CheckCircle2,
  Zap,
  Shield,
  Clock,
  FileSpreadsheet,
  BarChart3,
  AlertTriangle,
  Palmtree,
  Building2,
  Coffee,
  Briefcase,
  ShoppingBag,
  Dumbbell,
  Stethoscope,
  Wrench,
  Globe,
  
  Check,
} from 'lucide-react';

function AnimatedSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${isVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0 translate-y-6'}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const coreFeatures = [
  { icon: Zap, titleKey: 'lp.core1.title', descKey: 'lp.core1.desc' },
  { icon: FileSpreadsheet, titleKey: 'lp.core2.title', descKey: 'lp.core2.desc' },
  { icon: Palmtree, titleKey: 'lp.core3.title', descKey: 'lp.core3.desc' },
  { icon: Shield, titleKey: 'lp.core4.title', descKey: 'lp.core4.desc' },
  { icon: Globe, titleKey: 'lp.core5.title', descKey: 'lp.core5.desc' },
  { icon: Building2, titleKey: 'lp.core6.title', descKey: 'lp.core6.desc' },
];

const problemKeys = [
  { icon: AlertTriangle, key: 'lp.prob1', descKey: 'lp.prob1.desc' },
  { icon: Clock, key: 'lp.prob2', descKey: 'lp.prob2.desc' },
  { icon: Calendar, key: 'lp.prob3', descKey: 'lp.prob3.desc' },
  { icon: BarChart3, key: 'lp.prob4', descKey: 'lp.prob4.desc' },
  { icon: Users, key: 'lp.prob5', descKey: 'lp.prob5.desc' },
];

const pricingItems = [
  'lp.price7', 'lp.price1', 'lp.price3', 'lp.price4', 'lp.price2',
];

const audienceItems = [
  { icon: ShoppingBag, key: 'lp.aud1', descKey: 'lp.aud1.desc' },
  { icon: Coffee, key: 'lp.aud2', descKey: 'lp.aud2.desc' },
  { icon: Stethoscope, key: 'lp.aud3', descKey: 'lp.aud3.desc' },
  { icon: Dumbbell, key: 'lp.aud4', descKey: 'lp.aud4.desc' },
  { icon: Briefcase, key: 'lp.aud5', descKey: 'lp.aud5.desc' },
  { icon: Wrench, key: 'lp.aud6', descKey: 'lp.aud6.desc' },
];

const faqKeys = [
  { value: 'payroll', qKey: 'lp.faq.payroll.q', aKey: 'lp.faq.payroll.a' },
  { value: 'contracts', qKey: 'lp.faq.contracts.q', aKey: 'lp.faq.contracts.a' },
  { value: 'excel', qKey: 'lp.faq.excel.q', aKey: 'lp.faq.excel.a' },
  { value: 'security', qKey: 'lp.faq.security.q', aKey: 'lp.faq.security.a' },
  { value: 'stores', qKey: 'lp.faq.stores.q', aKey: 'lp.faq.stores.a' },
  { value: 'businesses', qKey: 'lp.faq.businesses.q', aKey: 'lp.faq.businesses.a' },
  { value: 'setup', qKey: 'lp.faq.setup.q', aKey: 'lp.faq.setup.a' },
  { value: 'selfService', qKey: 'lp.faq.selfService.q', aKey: 'lp.faq.selfService.a' },
  { value: 'perEmployee', qKey: 'lp.faq.perEmployee.q', aKey: 'lp.faq.perEmployee.a' },
  { value: 'afterTrial', qKey: 'lp.faq.afterTrial.q', aKey: 'lp.faq.afterTrial.a' },
  { value: 'languages', qKey: 'lp.faq.languages.q', aKey: 'lp.faq.languages.a' },
  
];



export default function Landing() {
  const { symbol, price } = useCurrency();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { organizationId } = useOrganization();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const monthlyPrice = Number(price);
  const annualMonthlyPrice = 82;
  const annualTotalPrice = 990;
  const annualPrice = annualTotalPrice;
  const displayPrice = billingCycle === 'monthly' ? price : annualMonthlyPrice;
  const perLabel = billingCycle === 'monthly' ? t('pricing.perMonth') : t('pricing.perMonth');

  const handleCheckout = async () => {
    if (!user || !organizationId) {
      navigate('/auth');
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { plan: billingCycle, organization_id: organizationId },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Checkout failed';
      toast.error(msg);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Operota',
    applicationCategory: 'BusinessApplication',
    description: 'AI staff scheduling and rota management platform for retail, hospitality, and shift-based businesses.',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: String(price),
      priceCurrency: 'EUR',
      description: '30-day free trial, then subscription',
    },
    url: 'https://www.operota.com',
  };

  return (
    <div className="min-h-screen bg-background scroll-smooth">
      <SeoHead
        title="Operota — AI Staff Scheduling Software for Shift-Based Teams"
        description="Operota is AI-powered scheduling software for any business with shift workers. Create rotas in seconds, track contract hours, manage leave, and stay compliant."
        canonical="https://www.operota.com/"
        jsonLd={jsonLd}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-card/80 backdrop-blur-md">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-4 sm:px-6 h-24">
          <img src={logo} alt="Operota" className="h-28" width={112} height={112} />
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button variant="ghost" asChild>
              <Link to="/auth">{t('header.signIn')}</Link>
            </Button>
            <Button asChild>
              <Link to="/auth">{t('hero.cta')}</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
      {/* ───── 1. HERO ───── */}
      <section className="relative py-24 sm:py-32 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.08)_0%,transparent_70%)]" />
        <div className="relative mx-auto max-w-3xl text-center">
          <AnimatedSection>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground">
              {t('lp.hero.title')}
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {t('lp.hero.subtitle')}
            </p>
          </AnimatedSection>

          {/* Language flags */}
          <AnimatedSection delay={150}>
            <div className="mt-6">
              <p className="text-[13px] text-muted-foreground/60">English · Italiano · Español · Français · Deutsch · Português</p>
              <p className="mt-1 text-xs text-muted-foreground/70">{t('lp.hero.langLine')}</p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex flex-col items-center gap-1">
                <Button size="lg" asChild className="shadow-lg shadow-primary/20">
                  <Link to="/auth">
                    {t('hero.cta')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                
              </div>
              <Button size="lg" variant="outline" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
                {t('lp.hero.demo')}
              </Button>
            </div>
          </AnimatedSection>

          {/* Trust badges */}
          <AnimatedSection delay={250}>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm text-foreground/80 font-medium">
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" />{t('lp.hero.trustBadge1')}</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" />{t('lp.hero.trustBadge2')}</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" />{t('lp.hero.trustBadge3')}</span>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={300}>
            <p className="mt-6 text-sm text-muted-foreground/70 font-medium tracking-wide">
              {t('lp.hero.trust')}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ───── Product Screenshot ───── */}
      <section className="py-16 px-4 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <AnimatedSection>
            <div className="relative mx-auto">
              <div className="rounded-xl border bg-card shadow-2xl shadow-primary/10 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/60 border-b">
                  <span className="h-3 w-3 rounded-full bg-destructive/60" />
                  <span className="h-3 w-3 rounded-full bg-yellow-400/60" />
                  <span className="h-3 w-3 rounded-full bg-green-400/60" />
                  <span className="ml-3 text-xs text-muted-foreground font-medium">app.operota.com</span>
                </div>
                <ScheduleMockup />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ───── 2. PROBLEM ───── */}
      <section className="py-20 px-4 sm:px-6 border-b">
        <div className="mx-auto max-w-4xl">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-center text-foreground">
              {t('lp.prob.title')}
            </h2>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {problemKeys.map((p) => (
                <div key={p.key} className="flex flex-col gap-1.5 rounded-lg border bg-card p-4">
                  <div className="flex items-center gap-3">
                    <p.icon className="h-5 w-5 text-destructive/70 shrink-0" />
                    <span className="text-sm font-medium text-foreground">{t(p.key)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground ml-8">{t(p.descKey)}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <p className="mt-10 text-center text-muted-foreground font-medium">
              {t('lp.prob.closing')}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ───── 3. CORE FEATURES ───── */}
      <section id="features" className="py-20 px-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-center text-foreground">{t('lp.core.sectionTitle')}</h2>
            <p className="mt-3 text-center text-muted-foreground max-w-2xl mx-auto">
              {t('lp.core.subtitle')}
            </p>
          </AnimatedSection>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreFeatures.map((f, i) => (
              <AnimatedSection key={f.titleKey} delay={i * 100}>
                <div className="group rounded-xl border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 relative overflow-hidden h-full">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-primary/40 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <f.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-foreground">{t(f.titleKey)}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(f.descKey)}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───── 4. WHO IT'S FOR ───── */}
      <section className="py-20 px-4 sm:px-6 bg-muted/30 border-y">
        <div className="mx-auto max-w-4xl">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-center text-foreground">{t('lp.aud.title')}</h2>
            <p className="mt-3 text-center text-muted-foreground max-w-xl mx-auto">
              {t('lp.aud.subtitle')}
            </p>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6">
              {audienceItems.map((a) => (
                <div key={a.key} className="flex flex-col items-center gap-2.5 text-center">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/8 text-primary">
                    <a.icon className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-medium text-foreground">{t(a.key)}</span>
                  <span className="text-xs text-muted-foreground">{t(a.descKey)}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ───── 5. AI SCHEDULING + COMPLIANCE ───── */}
      <section className="py-20 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-center text-foreground">{t('lp.section5.title')}</h2>
            <p className="mt-3 text-center text-muted-foreground max-w-2xl mx-auto">
              {t('lp.section5.subtitle')}
            </p>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold text-foreground">{t('lp.section5.card1.title')}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t('lp.section5.card1.desc')}</p>
              </div>
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold text-foreground">{t('lp.section5.card2.title')}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t('lp.section5.card2.desc')}</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ───── 6. PRICING ───── */}
      <section id="pricing" className="py-20 bg-muted/50 px-4 sm:px-6">
        <div className="mx-auto max-w-3xl">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-center text-foreground">{t('lp.pricing.title')}</h2>
          </AnimatedSection>

          {/* Billing toggle */}
          <AnimatedSection delay={100}>
            <div className="mt-8 flex items-center justify-center gap-3">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('pricing.monthly')}
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                  billingCycle === 'annual'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('pricing.annual')}
                <Badge variant="secondary" className="ml-2 text-xs">{billingCycle === 'annual' ? t('lp.pricing.annualBadge') : t('lp.pricing.bestValue')}</Badge>
              </button>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={150}>
            <div className="mt-10 rounded-2xl border-2 border-primary bg-card p-8 sm:p-10 relative overflow-hidden shadow-lg shadow-primary/10">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary to-primary/50" />
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-extrabold text-foreground">{symbol}{displayPrice}</span>
                    <span className="text-muted-foreground">{perLabel}</span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className="mt-1 text-sm font-medium text-green-600">{t('lp.pricing.annualSave')}</p>
                  )}
                  <p className="mt-2 text-sm text-muted-foreground">{t('pricing.afterTrial')}</p>
                </div>
                <div className="flex flex-col items-center sm:items-end gap-2 shrink-0">
                  <Button
                    size="lg"
                    className="shadow-lg shadow-primary/20"
                    onClick={handleCheckout}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? 'Redirecting…' : t('hero.cta')}
                    {!checkoutLoading && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="mt-8 border-t pt-6">
                <p className="text-sm font-medium text-foreground mb-4">{t('pricing.included')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {pricingItems.map((key) => (
                    <div key={key} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      {t(key)}
                    </div>
                  ))}
                </div>
              </div>
              <p className="mt-6 text-center text-xs text-muted-foreground">{t('lp.pricing.noFees')}</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ───── 7. EARLY ACCESS ───── */}
      <section className="py-20 px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-foreground">{t('lp.early.title')}</h2>
            <p className="mt-4 text-lg text-muted-foreground">{t('lp.early.subtitle')}</p>
            <div className="mt-8">
              <Button size="lg" asChild className="shadow-lg shadow-primary/20">
                <Link to="/auth">
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ───── 8. FAQ ───── */}
      <section className="py-20 px-4 sm:px-6 border-t">
        <div className="mx-auto max-w-3xl">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-center text-foreground">{t('faq.title')}</h2>
          </AnimatedSection>
          <AnimatedSection delay={150}>
            <Accordion type="single" collapsible className="mt-12">
              {faqKeys.map((faq) => (
                <AccordionItem key={faq.value} value={faq.value}>
                  <AccordionTrigger>{t(faq.qKey)}</AccordionTrigger>
                  <AccordionContent>{t(faq.aKey)}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimatedSection>
        </div>
      </section>

      {/* ───── Final CTA ───── */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 relative overflow-hidden bg-[hsl(220_40%_11%)]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(220_60%_20%/0.6)_0%,transparent_50%),radial-gradient(ellipse_at_bottom_left,hsl(250_40%_18%/0.5)_0%,transparent_50%)]" />
        <div className="relative mx-auto max-w-3xl text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              {t('finalCta.title')}
            </h2>
            <p className="mt-5 text-lg text-white/70 max-w-xl mx-auto leading-relaxed">
              {t('finalCta.subtitle')}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" asChild className="bg-white text-[hsl(220_40%_11%)] hover:bg-white/90 shadow-lg shadow-white/10">
                <Link to="/auth">
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="ghost" asChild className="border border-white/25 text-white hover:bg-white/10 hover:text-white">
                <Link to="/auth">{t('finalCta.signIn')}</Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      </main>

      {/* ───── Footer ───── */}
      <footer className="border-t py-12 px-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-8">
            <div>
              <img src={logo} alt="Operota" className="h-20" width={80} height={80} />
              <p className="text-sm text-muted-foreground mt-2">{t('footer.rights', { year: new Date().getFullYear().toString() })}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">{t('footer.product')}</h3>
              <nav className="flex flex-col gap-2 text-sm text-muted-foreground">
                <a href="#features" className="hover:text-foreground transition-colors">{t('footer.features')}</a>
                <a href="#pricing" className="hover:text-foreground transition-colors">{t('lp.pricing.title')}</a>
                <Link to="/auth" className="hover:text-foreground transition-colors">{t('footer.signIn')}</Link>
              </nav>
            </div>
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">{t('footer.solutions')}</h3>
              <nav className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/rota-maker" className="hover:text-foreground transition-colors">Rota Maker</Link>
                <Link to="/staff-scheduling" className="hover:text-foreground transition-colors">Staff Scheduling</Link>
                <Link to="/employee-scheduling-software" className="hover:text-foreground transition-colors">Employee Scheduling</Link>
                <Link to="/retail-staff-scheduling" className="hover:text-foreground transition-colors">Retail Scheduling</Link>
              </nav>
            </div>
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">{t('footer.legal')}</h3>
              <nav className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/terms" className="hover:text-foreground transition-colors">{t('footer.terms')}</Link>
                <Link to="/privacy" className="hover:text-foreground transition-colors">{t('footer.privacy')}</Link>
                <Link to="/cookies" className="hover:text-foreground transition-colors">{t('footer.cookies')}</Link>
                <Link to="/dpa" className="hover:text-foreground transition-colors">{t('footer.dpa')}</Link>
                <Link to="/refunds" className="hover:text-foreground transition-colors">{t('footer.refunds')}</Link>
              </nav>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
